import { CloseFullscreenRounded } from "@mui/icons-material";
import {
    Dialog,
    DialogContent,
    Divider,
    IconButton,
    Stack,
    Tooltip,
    Typography,
} from "@mui/material";
import { observer } from "mobx-react";
import { forwardRef, type ReactNode } from "react";
import { useStore } from "utils/useStore";
import { LLMClassifierBuilderStore } from "../../Stores/LLMClassifierBuilderStore";
import { LLMClassifierVersionComparison } from "./LLMClassifierVersionComparison";
import { VersionComparisonHeader } from "./VersionComparisonHeader";

export const VersionComparisonDialog = observer(
    forwardRef<
        HTMLDivElement,
        {
            isOpen: boolean;
            modelName: string;
            onClose: () => void;
            footer?: ReactNode;
        }
    >(function VersionComparisonDialog(props, scrollRef) {
        const store = useStore(LLMClassifierBuilderStore);
        const versions = store.llmClassifierVersions;
        const { before, after } = store.comparisonVersions;

        return (
            <Dialog
                fullScreen
                open={props.isOpen}
                onClose={props.onClose}
                aria-labelledby="version-comparison-dialog-title"
            >
                {/* Name and pickers share one row, and the pickers stay put while the diff scrolls. */}
                <Stack
                    direction="row"
                    alignItems="center"
                    gap={2}
                    px={3}
                    py={1.5}
                    borderBottom={1}
                    borderColor="gray.200"
                >
                    <Typography
                        id="version-comparison-dialog-title"
                        fontSize="18px"
                        fontWeight={600}
                        noWrap
                    >
                        {props.modelName}
                    </Typography>
                    {before && after && (
                        <>
                            <Divider orientation="vertical" flexItem />
                            <VersionComparisonHeader
                                versions={versions}
                                beforeVersion={before}
                                afterVersion={after}
                                onChange={(side, versionId) =>
                                    store.setComparisonVersion(side, versionId)
                                }
                            />
                        </>
                    )}
                    <Tooltip title="Exit full screen">
                        <IconButton
                            size="small"
                            aria-label="Exit full screen"
                            onClick={props.onClose}
                            sx={{ ml: "auto", color: "gray.600" }}
                        >
                            <CloseFullscreenRounded fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </Stack>

                <DialogContent
                    ref={scrollRef}
                    sx={(theme) => ({
                        p: 0,
                        display: "flex",
                        flexDirection: "column",
                        backgroundColor: theme.palette.background.default,
                    })}
                >
                    {/* Grows so the bar sits at the bottom even when the diff is short. */}
                    <Stack flex="1 0 auto" px={3} py={2}>
                        <LLMClassifierVersionComparison hideHeader />
                    </Stack>
                    {props.footer && (
                        <Stack
                            sx={(theme) => ({
                                position: "sticky",
                                bottom: 0,
                                // Beats the z-index 2 that react-diff-viewer puts on
                                // its own sticky column headers.
                                zIndex: 10,
                                px: 3,
                                pt: 2,
                                pb: 2,
                                background: `linear-gradient(to top, ${theme.palette.background.default} 70%, transparent)`,
                            })}
                        >
                            {props.footer}
                        </Stack>
                    )}
                </DialogContent>
            </Dialog>
        );
    }),
);
